import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Briefcase, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, FileSearch, FileText, Info, Key, Loader2, Mic, Phone, Plus, Scan, Search, Send, Target, Users, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const InvestigatorPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('new'); // new, plans, services, chat
  const [caseTypes, setCaseTypes] = useState([]);
  const [form, setForm] = useState({
    subject_name: '',
    subject_type: 'individual',
    case_type: 'personal_injury',
    role: 'defense_attorney',
    jurisdiction: '',
    case_summary: '',
    known_claims: [],
    additional_context: ''
  });
  const [newClaim, setNewClaim] = useState('');
  const [plan, setPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [liveServices, setLiveServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showLiveRequest, setShowLiveRequest] = useState(false);
  const [liveRequestForm, setLiveRequestForm] = useState({
    request_type: 'consultation',
    urgency: 'standard',
    details: '',
    contact_preference: 'email',
    budget_range: ''
  });
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('investigator', 'view');
    loadCaseTypes();
    loadPlans();
    loadLiveServices();
  }, [trackEvent]);
  
  const loadCaseTypes = async () => {
    try {
      const res = await axios.get(`${API}/investigator/case-types`);
      setCaseTypes(res.data);
    } catch (e) {
      console.error('Failed to load case types:', e);
    }
  };
  
  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/investigator/plans`);
      setPlans(res.data.plans || []);
    } catch (e) {
      console.error('Failed to load plans:', e);
    }
  };
  
  const loadLiveServices = async () => {
    try {
      const res = await axios.get(`${API}/investigator/live-services`);
      setLiveServices(res.data);
    } catch (e) {
      console.error('Failed to load live services:', e);
    }
  };
  
  const addClaim = () => {
    if (!newClaim.trim()) return;
    setForm(f => ({ ...f, known_claims: [...f.known_claims, newClaim.trim()] }));
    setNewClaim('');
  };
  
  const removeClaim = (idx) => {
    setForm(f => ({ ...f, known_claims: f.known_claims.filter((_, i) => i !== idx) }));
  };
  
  const generatePlan = async () => {
    if (!form.subject_name.trim()) {
      setError("Please enter a subject name to investigate");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/investigator/generate-plan`, form);
      setPlan(res.data);
      trackEvent('investigator', 'plan_generated', { case_type: form.case_type });
      loadPlans(); // Refresh list
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate investigation plan");
    } finally {
      setLoading(false);
    }
  };
  
  const loadPlan = async (investigation_id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/investigator/plans/${investigation_id}`);
      setPlan(res.data);
      // Load chat history
      const chatRes = await axios.get(`${API}/investigator/chat/${investigation_id}`);
      setChatMessages(chatRes.data.messages || []);
    } catch (e) {
      setError("Failed to load investigation plan");
    } finally {
      setLoading(false);
    }
  };
  
  const updateChecklistItem = async (idx, status) => {
    if (!plan) return;
    try {
      await axios.put(`${API}/investigator/plans/${plan.investigation_id}/checklist/${idx}?status=${status}`);
      setPlan(p => ({
        ...p,
        investigation_checklist: p.investigation_checklist.map((item, i) => 
          i === idx ? { ...item, status } : item
        )
      }));
    } catch (e) {
      console.error('Failed to update checklist:', e);
    }
  };
  
  const sendChatMessage = async () => {
    if (!chatInput.trim() || !plan) return;
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(msgs => [...msgs, { user_message: userMessage, timestamp: new Date().toISOString() }]);
    
    try {
      const res = await axios.post(`${API}/investigator/chat`, {
        investigation_id: plan.investigation_id,
        message: userMessage
      });
      setChatMessages(msgs => [...msgs.slice(0, -1), { 
        user_message: userMessage, 
        assistant_response: res.data.response,
        timestamp: res.data.timestamp 
      }]);
    } catch (e) {
      setError("Failed to send message");
    }
  };
  
  const submitLiveRequest = async () => {
    if (!plan || !liveRequestForm.details.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/investigator/live-request`, {
        investigation_id: plan.investigation_id,
        ...liveRequestForm
      });
      setShowLiveRequest(false);
      setLiveRequestForm({ request_type: 'consultation', urgency: 'standard', details: '', contact_preference: 'email', budget_range: '' });
      alert('Your request has been submitted. A licensed investigator will contact you shortly.');
    } catch (e) {
      setError("Failed to submit request");
    } finally {
      setLoading(false);
    }
  };
  
  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'text-red-600 bg-red-50 border-red-200';
    if (priority >= 5) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500';
      case 'in_progress': return 'bg-blue-500';
      case 'blocked': return 'bg-red-500';
      default: return 'bg-slate-300';
    }
  };

  return (
    <main className="min-h-screen" style={{ background: '#F8FAFC' }} data-testid="investigator-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center">
                <Scan className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif", color: '#1E293B' }}>The Investigator</h1>
                <p className="text-sm" style={{ color: '#475569' }}>Opposition research & evidence discovery</p>
              </div>
            </div>
            {plan && (
              <Button variant="outline" onClick={() => { setPlan(null); setChatMessages([]); }}>
                <Plus className="h-4 w-4 mr-2" /> New Investigation
              </Button>
            )}
          </div>
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="new" data-testid="tab-new">New Investigation</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">My Plans ({plans.length})</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Live Investigator</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" disabled={!plan}>AI Assistant</TabsTrigger>
          </TabsList>
          
          {/* NEW INVESTIGATION TAB */}
          <TabsContent value="new" className="mt-6">
            {!plan ? (
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Search className="h-5 w-5 text-red-600" />
                        Investigation Target
                      </CardTitle>
                      <CardDescription>Enter the person or corporation you need to investigate</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label>Subject Name *</Label>
                          <Input 
                            placeholder="John Smith or ABC Corporation"
                            value={form.subject_name}
                            onChange={e => setForm(f => ({ ...f, subject_name: e.target.value }))}
                            data-testid="subject-name-input"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Subject Type</Label>
                          <Select value={form.subject_type} onValueChange={v => setForm(f => ({ ...f, subject_type: v }))}>
                            <SelectTrigger data-testid="subject-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {caseTypes.subject_types?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label>Case Type</Label>
                          <Select value={form.case_type} onValueChange={v => setForm(f => ({ ...f, case_type: v }))}>
                            <SelectTrigger data-testid="case-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {caseTypes.case_types?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Your Role</Label>
                          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                            <SelectTrigger data-testid="role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {caseTypes.roles?.map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Jurisdiction</Label>
                        <Input 
                          placeholder="e.g., California, New York"
                          value={form.jurisdiction}
                          onChange={e => setForm(f => ({ ...f, jurisdiction: e.target.value }))}
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Case Summary</Label>
                        <Textarea 
                          placeholder="Brief description of the case facts..."
                          value={form.case_summary}
                          onChange={e => setForm(f => ({ ...f, case_summary: e.target.value }))}
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Known Claims by Opposing Party</Label>
                        <div className="flex gap-2">
                          <Input 
                            placeholder="Add a claim they're making..."
                            value={newClaim}
                            onChange={e => setNewClaim(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addClaim()}
                          />
                          <Button type="button" onClick={addClaim} variant="outline">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {form.known_claims.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {form.known_claims.map((claim, i) => (
                              <Badge key={i} variant="secondary" className="gap-1">
                                {claim}
                                <button onClick={() => removeClaim(i)} className="ml-1 hover:text-red-600">
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Additional Context</Label>
                        <Textarea 
                          placeholder="Any other relevant information..."
                          value={form.additional_context}
                          onChange={e => setForm(f => ({ ...f, additional_context: e.target.value }))}
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Button 
                    onClick={generatePlan} 
                    disabled={loading || !form.subject_name.trim()}
                    className="w-full h-12 text-lg font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #DC2626, #EF4444)' }}
                    data-testid="generate-plan-btn"
                  >
                    {loading ? (
                      <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating Investigation Plan...</>
                    ) : (
                      <><Scan className="h-5 w-5 mr-2" /> Generate Investigation Plan</>
                    )}
                  </Button>
                </div>
                
                {/* Sidebar - What You'll Get */}
                <div className="space-y-6">
                  <Card className="border-red-100 bg-red-50/50">
                    <CardHeader>
                      <CardTitle className="text-red-800 text-lg">What You'll Get</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[
                        { icon: Target, text: "Vulnerability Points to Exploit" },
                        { icon: FileSearch, text: "Discovery Targets & Requests" },
                        { icon: Search, text: "Public Record Search Guide" },
                        { icon: Users, text: "Social Media Investigation Tips" },
                        { icon: ClipboardList, text: "Priority Investigation Checklist" },
                        { icon: Briefcase, text: "Potential Witnesses to Locate" }
                      ].map(({ icon: Icon, text }) => (
                        <div key={text} className="flex items-center gap-2 text-sm text-red-700">
                          <Icon className="h-4 w-4" />
                          {text}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Need a Live Investigator?
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect with licensed private investigators for background checks, surveillance, asset searches, and more.
                      </p>
                      <Button variant="outline" onClick={() => setActiveTab('services')} className="w-full">
                        View Services
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              /* INVESTIGATION PLAN RESULTS */
              <div className="space-y-6">
                {/* Header */}
                <Card className="border-red-200 bg-gradient-to-r from-red-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className="mb-2 bg-red-600">Investigation Plan</Badge>
                        <h2 className="text-2xl font-bold" style={{ color: '#1E293B' }}>{plan.subject?.name}</h2>
                        <p className="text-sm" style={{ color: '#475569' }}>
                          {plan.subject?.type?.replace('_', ' ')} • {plan.subject?.case_type?.replace('_', ' ')} • {plan.role?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-red-600">{plan.confidence_score}%</div>
                        <p className="text-xs text-muted-foreground">Confidence Score</p>
                        <p className="text-xs text-muted-foreground">{plan.models_consulted} AI models consulted</p>
                      </div>
                    </div>
                    {plan.executive_summary && (
                      <p className="mt-4 text-sm p-3 rounded-lg bg-white border" style={{ color: '#475569' }}>
                        {plan.executive_summary}
                      </p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Vulnerability Points */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-red-600" />
                      Vulnerability Points to Exploit
                    </CardTitle>
                    <CardDescription>Key weaknesses and areas to investigate</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      {plan.vulnerability_points?.map((vp, i) => (
                        <div key={i} className={`p-4 rounded-lg border ${getPriorityColor(vp.priority)}`}>
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold">{vp.category}</h4>
                            <Badge variant="outline">Priority {vp.priority}/10</Badge>
                          </div>
                          <p className="text-sm mb-3">{vp.description}</p>
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs font-medium mb-1">Evidence to Seek:</p>
                              <ul className="text-xs list-disc pl-4 space-y-0.5">
                                {vp.evidence_to_seek?.slice(0, 3).map((e, j) => <li key={j}>{e}</li>)}
                              </ul>
                            </div>
                            <div>
                              <p className="text-xs font-medium mb-1">Potential Sources:</p>
                              <div className="flex flex-wrap gap-1">
                                {vp.potential_sources?.map((s, j) => (
                                  <Badge key={j} variant="secondary" className="text-xs">{s}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Discovery Targets */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSearch className="h-5 w-5 text-blue-600" />
                      Discovery Targets
                    </CardTitle>
                    <CardDescription>Documents, depositions, and subpoenas to request</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {plan.discovery_targets?.map((dt, i) => (
                        <div key={i} className="p-4 rounded-lg border bg-slate-50">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge>{dt.target_type}</Badge>
                              <h4 className="font-semibold">{dt.description}</h4>
                            </div>
                            <Badge variant="outline">Priority {dt.priority}/10</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{dt.rationale}</p>
                          {dt.suggested_requests?.length > 0 && (
                            <div className="bg-white p-3 rounded border">
                              <p className="text-xs font-medium mb-1">Suggested Request Language:</p>
                              <ul className="text-xs space-y-1">
                                {dt.suggested_requests.map((r, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 mt-0.5 text-blue-600" />
                                    {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Investigation Checklist */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-emerald-600" />
                      Investigation Checklist
                    </CardTitle>
                    <CardDescription>Track your investigation progress</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {plan.investigation_checklist?.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-slate-50">
                          <button 
                            onClick={() => updateChecklistItem(i, item.status === 'completed' ? 'pending' : 'completed')}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              item.status === 'completed' ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-500'
                            }`}
                          >
                            {item.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-white" />}
                          </button>
                          <div className="flex-1">
                            <p className={`font-medium ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {item.item}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                              <span className="text-xs text-muted-foreground">{item.estimated_effort}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className={getPriorityColor(item.priority)}>P{item.priority}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Public Records & Social Media */}
                <div className="grid gap-6 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-purple-600" />
                        Public Record Searches
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {plan.public_record_searches?.map((pr, i) => (
                        <div key={i} className="p-3 rounded-lg border bg-purple-50/50">
                          <h4 className="font-semibold text-sm">{pr.record_type}</h4>
                          <p className="text-xs text-muted-foreground mb-2">Source: {pr.source}</p>
                          <p className="text-xs mb-2">{pr.search_instructions}</p>
                          <div className="flex flex-wrap gap-1">
                            {pr.what_to_look_for?.map((item, j) => (
                              <Badge key={j} variant="outline" className="text-xs">{item}</Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-cyan-600" />
                        Social Media Investigation
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {plan.social_media_guidance?.map((sm, i) => (
                        <div key={i} className="p-3 rounded-lg border bg-cyan-50/50">
                          <h4 className="font-semibold text-sm">{sm.platform}</h4>
                          <div className="mt-2 space-y-2">
                            <div>
                              <p className="text-xs font-medium">Red Flags:</p>
                              <ul className="text-xs list-disc pl-4">
                                {sm.red_flags_to_look_for?.slice(0, 3).map((rf, j) => <li key={j}>{rf}</li>)}
                              </ul>
                            </div>
                            <p className="text-xs text-muted-foreground italic">{sm.preservation_notes}</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Key Questions & Witnesses */}
                <div className="grid gap-6 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Key Questions to Answer</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {plan.key_questions?.map((q, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">
                              {i + 1}
                            </div>
                            {q}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Potential Witnesses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {plan.potential_witnesses?.map((w, i) => (
                          <Badge key={i} variant="secondary" className="text-sm py-1.5">{w}</Badge>
                        ))}
                      </div>
                      {plan.timeline_recommendations && (
                        <div className="mt-4 p-3 rounded-lg bg-slate-50 border">
                          <p className="text-xs font-medium mb-1">Timeline:</p>
                          <p className="text-sm">{plan.timeline_recommendations}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Actions */}
                <div className="flex gap-4">
                  <Button onClick={() => setActiveTab('chat')} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    <Mic className="h-4 w-4 mr-2" /> Ask AI Assistant
                  </Button>
                  <Button onClick={() => setShowLiveRequest(true)} variant="outline" className="flex-1">
                    <Users className="h-4 w-4 mr-2" /> Request Live Investigator
                  </Button>
                </div>
                
                {/* Disclaimer */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {plan.disclaimer}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </TabsContent>
          
          {/* MY PLANS TAB */}
          <TabsContent value="plans" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>My Investigation Plans</CardTitle>
                <CardDescription>View and manage your investigation plans</CardDescription>
              </CardHeader>
              <CardContent>
                {plans.length === 0 ? (
                  <div className="text-center py-12">
                    <Scan className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No investigation plans yet</p>
                    <Button onClick={() => setActiveTab('new')} className="mt-4">
                      Start Your First Investigation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {plans.map((p) => (
                      <div 
                        key={p.investigation_id} 
                        className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 cursor-pointer"
                        onClick={() => loadPlan(p.investigation_id)}
                      >
                        <div>
                          <h4 className="font-semibold">{p.subject?.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {p.subject?.case_type?.replace('_', ' ')} • {p.role?.replace('_', ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(p.generated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-lg font-bold text-red-600">{p.confidence_score}%</div>
                            <p className="text-xs text-muted-foreground">Confidence</p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* LIVE SERVICES TAB */}
          <TabsContent value="services" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {liveServices.services?.map((service) => (
                <Card key={service.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">{service.name}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Turnaround:</span>
                        <span className="font-medium">{service.turnaround}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price Range:</span>
                        <span className="font-medium text-emerald-600">{service.price_range}</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      variant="outline"
                      onClick={() => {
                        setLiveRequestForm(f => ({ ...f, request_type: service.id }));
                        setShowLiveRequest(true);
                      }}
                    >
                      Request Service
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* CHAT TAB */}
          <TabsContent value="chat" className="mt-6">
            {plan ? (
              <Card className="h-[600px] flex flex-col">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <Mic className="h-5 w-5 text-blue-600" />
                    AI Investigation Assistant
                  </CardTitle>
                  <CardDescription>Ask follow-up questions about your investigation: {plan.subject?.name}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Mic className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Ask me anything about your investigation</p>
                      <p className="text-sm mt-2">Examples: "What red flags should I look for?" or "How do I get bank records?"</p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className="space-y-3">
                      {msg.user_message && (
                        <div className="flex justify-end">
                          <div className="bg-blue-600 text-white rounded-lg px-4 py-2 max-w-[80%]">
                            {msg.user_message}
                          </div>
                        </div>
                      )}
                      {msg.assistant_response && (
                        <div className="flex justify-start">
                          <div className="bg-slate-100 rounded-lg px-4 py-2 max-w-[80%] whitespace-pre-wrap">
                            {msg.assistant_response}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Ask about your investigation..."
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
                      data-testid="chat-input"
                    />
                    <Button onClick={sendChatMessage} disabled={!chatInput.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Scan className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Generate an investigation plan first to use the AI assistant</p>
                  <Button onClick={() => setActiveTab('new')} className="mt-4">
                    Start Investigation
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Live Investigator Request Modal */}
        {showLiveRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowLiveRequest(false)}>
            <Card className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle>Request Live Investigator</CardTitle>
                <CardDescription>Connect with a licensed private investigator</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Service Type</Label>
                  <Select value={liveRequestForm.request_type} onValueChange={v => setLiveRequestForm(f => ({ ...f, request_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {liveServices.services?.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1.5">
                  <Label>Urgency</Label>
                  <Select value={liveRequestForm.urgency} onValueChange={v => setLiveRequestForm(f => ({ ...f, urgency: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (24-48 hours)</SelectItem>
                      <SelectItem value="expedited">Expedited (4-12 hours) +50%</SelectItem>
                      <SelectItem value="urgent">Urgent (1-4 hours) +100%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1.5">
                  <Label>Contact Preference</Label>
                  <Select value={liveRequestForm.contact_preference} onValueChange={v => setLiveRequestForm(f => ({ ...f, contact_preference: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="video_call">Video Call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1.5">
                  <Label>Details</Label>
                  <Textarea 
                    placeholder="Describe what you need investigated..."
                    value={liveRequestForm.details}
                    onChange={e => setLiveRequestForm(f => ({ ...f, details: e.target.value }))}
                    rows={4}
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label>Budget Range (Optional)</Label>
                  <Input 
                    placeholder="e.g., $500-$1,000"
                    value={liveRequestForm.budget_range}
                    onChange={e => setLiveRequestForm(f => ({ ...f, budget_range: e.target.value }))}
                  />
                </div>
              </CardContent>
              <div className="p-6 pt-0 flex gap-3">
                <Button variant="outline" onClick={() => setShowLiveRequest(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={submitLiveRequest} disabled={loading || !liveRequestForm.details.trim()} className="flex-1 bg-red-600 hover:bg-red-700">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Request'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default InvestigatorPage;
